import _ from 'underscore';
import Promise from 'bluebird';
import { TargetingSettingsContext } from './targeting-settings-context';
import { TargetingScope, TargetingLevel } from './config';
import { getDeviceCriterionModel } from './helpers';

export class CommonRepository {
  constructor({ deps }) {
    const scopeResources = {
      [TargetingScope.Account]: TargetingSettingsContext.edmCampaign.currentAccount,
      [TargetingScope.Campaign]: TargetingSettingsContext.edmCampaign.currentCampaign,
      [TargetingScope.AdGroup]: TargetingSettingsContext.edmCampaign.currentAdGroup,
    };

    this.resource = _.result(scopeResources, deps.scope);

    if (deps.level === TargetingLevel.AdGroup) {
      this.jsDataResource = TargetingSettingsContext.jsDataCampaign.AdGroupCriterion;
      this.entities = this.resource.AdGroupCriterions;
    } else {
      this.jsDataResource = TargetingSettingsContext.jsDataCampaign.CampaignCriterion;
      this.entities = this.resource.CampaignCriterions;
    }

    this.jsDataResource = _.defaults({
      idAttribute: 'VirtualId',
    }, this.jsDataResource);

    this.scope = deps.scope;
    this.level = deps.level;

    const { store } = TargetingSettingsContext.edmCampaign.jsdata;
    store.registerAdapter('bulk', new TargetingSettingsContext.BulkAdapter(), { default: false });

    this.afterFindAllCallback = _.isFunction(deps.afterFindAllCallback)
      ? deps.afterFindAllCallback : null;
  }

  getDataSource() {
    return {
      type: 'jsdata',
      entity: this.jsDataResource,
      options: {
        entitySet: this.entities['Default.GridData'].$call(this.parameters),
      },
    };
  }

  get query() {
    if (this.level === TargetingLevel.AdGroup) {
      return {
        $expand: 'Campaign, AdGroup',
      };
    }
    return {
      $expand: 'Campaign',
    };
  }

  get filterOptions() {
    return {
      odata: {
        custom: {
          Criterion: (operand, value) => `${operand}(Criterion, ${value})`,
        },
      },
    };
  }

  getQuery() {
    return {
      query: this.query,
      options: {
        cacheResponse: false,
        entitySet: this.entities['Default.GridData'].$call(this.parameters),
        headers: {
          lcid: TargetingSettingsContext.pageContext.CultureLcid,
        },
        filterOptions: this.filterOptions,
        afterFindAll: this.afterFindAllCallback,
      },
    };
  }

  getAdGroupEntitySetByScopeAndLevel({ campaignId, adgroupId }) {
    const entitySetMap = {
      [TargetingScope.Account]: () => this.resource.Campaigns.$withKey(campaignId)
        .AdGroups.$withKey(adgroupId).AdGroupCriterions,
      [TargetingScope.Campaign]: () => this.resource.AdGroups.$withKey(adgroupId)
        .AdGroupCriterions,
      [TargetingScope.AdGroup]: () => this.resource.AdGroupCriterions,
    };
    return _.result(entitySetMap, this.scope);
  }

  getCampaignEntitySetByScopeAndLevel({ campaignId }) {
    const { edmCampaign } = TargetingSettingsContext;
    const entitySetMap = {
      [TargetingScope.Account]: () => this.resource.Campaigns.$withKey(campaignId)
        .CampaignCriterions,
      [TargetingScope.Campaign]: () => this.resource.CampaignCriterions,
      [TargetingScope.AdGroup]: () => edmCampaign.currentCampaign.CampaignCriterions,
    };
    return _.result(entitySetMap, this.scope);
  }

  update(model) {
    // Use bulkupsert to inline edit bid for device type
    // When criterion id < 0, it's virtual targets in MT
    const isVirtualTarget = m => _.isNumber(m.Id) && m.Id < 0;
    if (model.Criterion.Type === 'Device' && (isVirtualTarget(model) || _.has(model, 'OSTargets'))) {
      const newModel = getDeviceCriterionModel(model, this.level);
      if (this.level === TargetingLevel.AdGroup) {
        return TargetingSettingsContext.jsDataCampaign.AdGroup.update(newModel.Id, newModel, { adapter: 'bulk', cacheResponse: false });
      }

      return TargetingSettingsContext.jsDataCampaign.Campaign.update(newModel.Id, newModel, { adapter: 'bulk', cacheResponse: false });
    }

    const campaignId = _.result(model.Campaign, 'Id') || TargetingSettingsContext.state.get('campaign_id');
    const entitySet = this.level === TargetingLevel.Campaign
      ? this.getCampaignEntitySetByScopeAndLevel({ campaignId })
      : this.getAdGroupEntitySetByScopeAndLevel({
        campaignId,
        adgroupId: model.AdGroup.Id,
      });
    const options = {
      entitySet,
      cacheResponse: false,
    };
    const attrs = _.pick(model, '@odata.type', 'CriterionBid', 'CriterionCashback');

    if (this.level === TargetingLevel.AdGroup) {
      attrs.Criterion = _.pick(model.Criterion, '@odata.type');
    }
    return this.jsDataResource.update(model.Id, attrs, options);
  }

  bulkUpdate(params) {
    const jsData = TargetingSettingsContext.jsDataCampaign;
    const entity = this.level === TargetingLevel.AdGroup ? jsData.AdGroup : jsData.Campaign;
    return entity.update(params.Id, params, { adapter: 'bulk', cacheResponse: false });
  }

  load({ campaignId, adgroupId, params }) {
    const entitySet = this.getCampaignEntitySetByScopeAndLevel({ campaignId, adgroupId })['Model.CampaignCriterion'];
    const options = {
      entitySet,
      cacheResponse: false,
      bypassCache: true,
    };
    if (this.level === TargetingLevel.AdGroup) {
      const adgroupEntitySet = this.getAdGroupEntitySetByScopeAndLevel({ campaignId, adgroupId })['Model.AdGroupCriterion'];
      const adgroupOptions = _.extend({}, options, { entitySet: adgroupEntitySet });
      return Promise.join(
        this.jsDataResource.findAll(params, adgroupOptions),
        TargetingSettingsContext.jsDataCampaign.CampaignCriterion.findAll(params, options),
        (adgroupTargets, campaignTargets) => ({ adgroupTargets, campaignTargets })
      );
    }

    return this.jsDataResource.findAll(params, options);
  }
}
